/**
 * @graphql cc
 */
import React, { useCallback, useState, useContext, useEffect, useRef, useMemo } from 'react';
import type { Dispatch, PropsWithChildren } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';
import { mergeRefs } from 'use-callback-ref';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import EditIcon from '@atlaskit/icon/core/edit';
import ScreenIcon from '@atlaskit/icon/core/screen';
import SettingsIcon from '@atlaskit/icon/core/settings';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import CommentIcon from '@atlaskit/icon/core/comment';
import { IconButton } from '@atlaskit/button/new';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import type { OnOpenChangeArgs, CustomTriggerProps } from '@atlaskit/dropdown-menu';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { ButtonItem } from '@atlaskit/menu';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import WrenchIcon from '@atlaskit/icon-lab/core/wrench';
import ChangesIcon from '@atlaskit/icon/core/changes';
import MarkdownIcon from '@atlaskit/icon/core/markdown';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import UndoIcon from '@atlaskit/icon/core/undo';
import RedoIcon from '@atlaskit/icon/core/redo';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import PresenterModeIcon from '@atlaskit/icon/core/presenter-mode';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import ClockIcon from '@atlaskit/icon/core/clock';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import FullscreenEnterIcon from '@atlaskit/icon/core/fullscreen-enter';
import Skeleton from '@atlaskit/skeleton';
import DownloadIcon from '@atlaskit/icon/core/download';
import SearchIcon from '@atlaskit/icon/core/search';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import { FeedbackType } from '@atlassian/company-hub-common';

import {
	PANEL_MODE_TEMPLATES,
	PANEL_MODE_IMPORT,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { LivePagesModeChanger } from '@confluence/live-pages-features/entry-points/LivePagesModeChanger.tsx';
import {
	COMPANY_HUB_EDIT,
	COMPANY_HUB_SETTINGS,
} from '@confluence/named-routes/entry-points/routes.ts';
import { isSpaceOverview } from '@confluence/named-routes/entry-points/routes-utils.ts';
import {
	useRouteActions,
	createSingleQueryParamHook,
	useHistory,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { MoveBlogModalLoader } from '@confluence/move-blog-modal';
import { Buttons } from '@confluence/editor-ellipsis-dropdown/entry-points/EditorEllipsisDropdown.ts';
import { onClickPageRevert } from '@confluence/editor-ellipsis-dropdown/entry-points/onClickPageRevert.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals, expVal, manuallyLogExpExposure } from '@confluence/feature-experiments';
import { DeleteNestedPagesActionLoader } from '@confluence/bulk-page-actions/entry-points/DeleteNestedPagesAction.ts';
import type {
	WebItemLocationQueryType,
	WebItemLocationQueryVariables,
} from '@confluence/web-item-location/entry-points/WebItemLocationQuery.ts';
import type { FormattedWebItem } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import { TOOLS_MENU_CONTAINER } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { WebItemLocationQuery } from '@confluence/web-item-location/entry-points/WebItemLocationQuery.ts';
import { WebItemLocation } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import { MovePageDialog } from '@confluence/page-tree/entry-points/MovePageDialog.tsx';
import { CopyPageTreeDialog } from '@confluence/copy-page-tree-dialog';
import { CopyPageDialog } from '@confluence/page-tree/entry-points/CopyPageDialog.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { UnarchivePagesDialogLoader } from '@confluence/archive-pages/entry-points/UnarchivePagesLoader.ts';
import { ArchiveUpsellDialog } from '@confluence/archive-pages/entry-points/ArchiveUpsellDialog.ts';
import { ArchivePagesLoader } from '@confluence/archive-pages/entry-points/ArchivePagesLoader.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	ARCHIVE_PAGES_EXPERIENCE,
	CONTENT_OVERFLOW_MENU,
	DELETE_PAGE_EXPERIENCE,
	MOVE_PAGE_DIALOG_LOAD_EXPERIENCE,
	PRESENTER_MODE_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceStop,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { GlobalFeedbackCollector } from '@confluence/global-feedback-collector/entry-points/GlobalFeedbackCollector.ts';
import {
	useMoreActionsMenuState,
	useMoreActionsMenuActions,
} from '@confluence/content-types-utils/entry-points/moreActionsMenuState.ts';
import { useVersionHistoryExperienceTracking } from '@confluence/content-types-utils/entry-points/useVersionHistoryExperienceTracking.ts';
import { useIsVersionHistoryEnabled } from '@confluence/content-types-utils/entry-points/useIsVersionHistoryEnabled.ts';
import {
	getIsCopyRendered,
	getIsMoveRendered,
	getIsExportRendered,
	getIsConvertRendered,
	getIsTemplatesRendered,
	getIsArchiveRendered,
	getIsPresentRendered,
	getIsSwitchToLiveEditRendered,
	getIsViewChangesRendered,
	getIsRevertRendered,
	getIsRevertToLegacyEditorRendered,
	getIsFeedbackRendered,
	getIsHelpRendered,
	getIsPreviewRendered,
	getIsContentOwnershipRendered,
	getIsConvertToPageRendered,
	getIsConvertToMarkdownRendered,
	getIsConvertToRichTextRendered,
	getIsWatchRendered,
	getIsPreferencesRendered,
	getIsAutomationsRendered,
	getIsAdvancedRendered,
	getIsAppsRendered,
	getIsPageHistoryRendered,
	getIsUndoRedoFindRendered,
} from '@confluence/content-types-utils/entry-points/renderedOverflowActions.ts';
import { useIsCommentsDialogEnabled } from '@confluence/content-types-utils/entry-points/useIsCommentsDialogEnabled.ts';
import { useIsStarringEnabled } from '@confluence/content-types-utils/entry-points/useIsStarringEnabled.ts';
import {
	CONTENT_TYPE_FEEDBACK_SOURCE,
	isCanvasContentType,
} from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { ConvertMarkdownModeDialogContainer } from '@confluence/content-markdown-mode/entry-points/ConvertMarkdownModeDialogContainer.ts';
import { useMarkdownModeActions } from '@confluence/content-markdown-mode/entry-points/useMarkdownModeActions.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { ResolvedInlineCommentsCounterLoader } from '@confluence/resolved-inline-comments-dialog/entry-points/ResolvedInlineCommentsCounter.ts';
import { ResolvedInlineCommentsDialogLoader } from '@confluence/resolved-inline-comments-dialog/entry-points/ResolvedInlineCommentsDialog.ts';
import { CONTENT_OWNERSHIP_WEB_ITEM_ID } from '@confluence/content-ownership/entry-points/contentOwnershipConstants.ts';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { EllipsisClassificationMenuItem } from '@confluence/data-classification/entry-points/ellipsisClassification.ts';
import type { ContentType as ShareContentType } from '@confluence/share-and-restrict-dialog/entry-points/ShareRestrictAndCopyCoordinator.tsx';
import {
	LazyPresenterModeContextConsumer,
	usePresenterModeContext,
} from '@confluence/presentation-mode';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	PRESENTATION_MODE_SHORTCUT,
	UNIVERSAL_PRESENTATION_MODE_SHORTCUT,
	UNIVERSAL_PRESENTATION_MODE_SHORTCUT_SAFARI,
	EDITOR2_SHORTCUTS,
	LINK_TO_THIS_PAGE_SHORTCUT,
	UNIVERSAL_LINK_TO_THIS_PAGE_SHORTCUT,
	UNIVERSAL_TOGGLE_MARKDOWN_MODE_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import {
	openMoreActionsMenuClick,
	openForgeAppFromSubmenu,
	simulateItemClick,
} from '@confluence/legacy-keyboard-shortcut-helpers/entry-points/legacyKeyboardShortcutHelpers.ts';
import { useThemingState } from '@confluence/theming/entry-points/useThemingState.ts';
import { getSystemPreferredColorScheme } from '@confluence/theming-utils/entry-points/systemColorScheme.ts';
import { OBJECT_SIDEBAR_TOUR_TARGETS } from '@confluence/object-sidebar-components/entry-points/objectSidebarTourTargets.tsx';
import {
	useMoreActionsObjectSidebarTour,
	MoreActionsObjectSidebarTourSteps,
} from '@confluence/object-sidebar-components/entry-points/useMoreActionsObjectSidebarTour.tsx';
import { RestrictionsDialogMenuPropsProvider } from '@confluence/restrictions/entry-points/RestrictionsDialogTrigger.ts';
import { LazyContentToolsItem } from '@confluence/content-tools/entry-points/ContentToolsButton.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';
import { LazyWatchSettingsOverflowMenuShortcut } from '@confluence/watch-dialog/entry-points/WatchSettingsOverflowMenuShortcut.tsx';
import { WatchSettingsItem } from '@confluence/watch-dialog/entry-points/WatchSettingsItem.tsx';
import { FavouriteMenuItem } from '@confluence/favourite-button/entry-points/FavouriteMenuItem.ts';
import { FavouriteContentShortcutListener } from '@confluence/favourite-button/entry-points/FavouriteContentShortcutListener.ts';
import {
	SpaceStarMenuItemWithQuery,
	SpaceStarShortcutListener,
} from '@confluence/action-buttons/entry-points/spaceStar.ts';
import { useFeedbackCollector } from '@confluence/company-hub-utils/entry-points/FeedbackCollectorLoader.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { RevertEditorDialog } from '@confluence/editor-conversion/entry-points/RevertEditorDialog.ts';
import type { ContentTypeType } from '@confluence/editor-features/entry-points/ContentTypeType.ts';
import { isSafari } from '@confluence/browser-helper';
import { useCollabNetworkStatus } from '@confluence/collab-network-status';
import { LinkToThisPageDialog } from '@confluence/link-to-this-page-dialog';
import { ForgeKeyboardShortcut } from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut.ts';
import { LazyForgeContentTools } from '@confluence/content-tools/entry-points/LazyForgeContentTools.ts';
import { useEmbeddedModernHeader } from '@confluence/embedded-confluence-allowed-features/entry-points/modern-header.ts';
import { LazySmartFolderQuickActionMenuItem } from '@confluence/smart-folder/entry-points/LazySmartFolderQuickActionMenuItem.ts';
import { isSmartFolder } from '@confluence/smart-folder/entry-points/isSmartFolder.ts';
import type { SmartFolderQuickActionAnalyticsId } from '@confluence/smart-folder/entry-points/SmartFolderQuickActionMenuItem.ts';
import { useIsSmartFoldersEnabledForMilestone } from '@confluence/smart-folder/entry-points/useIsSmartFoldersEnabledForMilestone.ts';

import type { HeaderFeaturesType } from '../ContentTypesHeaderFeatures.tsx';
import { useEditorObjectHeaderContext } from '../EditorObjectHeaderContext.tsx';
import type { ContentTypesHeaderQueryNode } from '../ContentTypesHeader.tsx';
import { useIsPageOrBlogAutoConverted } from '../useIsPageOrBlogAutoConverted.tsx';

import { ConvertToLivePageItem } from './components/ConvertToLivePageMenuItem.tsx';
import {
	ExpandableNestedMenuItem,
	type ExpandableNestedMenuItemItemType,
} from './components/ExpandableNestedMenuItem.tsx';
import { SlidesExportMenuItem } from './components/SlidesExportMenuItem.tsx';
import { WhiteboardToLiveDocMenuItem } from './components/WhiteboardToLiveDocMenuItem.tsx';
import {
	ArchiveDeleteMenuItem,
	CustomAction,
	CopyMenuItem,
	NestedDatabaseCopyMenu,
	ViewChangesItem,
	ContentOwnershipActionItem,
	NestedMenuTypes,
	ConvertMenuItem,
	ExportMenuItem,
	getAppsMenuItem,
	AutomationsMenuItem,
	ShortcutKeysVisual,
	ArchiveMenuItem,
	DeleteMenuItem,
	AdvancedMenuItem,
	// eslint-disable-next-line import/no-cycle
} from './components/SharedActionMenuItems.tsx';
import { ViewPageHistoryMenuItem } from './components/ViewPageHistoryMenuItem.tsx';
import { AskToEditMenuItem } from './components/AskToEditMenuItem.tsx';
import {
	ACTION_MOVE_CONTENT,
	ACTION_MOVE_BLOGPOST,
	ACTION_REMOVE_CONTENT,
	ACTION_COPY_CONTENT,
	ACTION_ARCHIVE_CONTENT,
	ACTIONS_RESTORE_CONTENT,
	ACTION_GIVE_FEEDBACK,
	ACTION_VIEW_RESOLVED_COMMENTS,
	ACTION_VIEW_VERSION_HISTORY,
	ACTION_VIEW_PAGE_HISTORY,
	ACTION_EXPORT_PDF,
	ACTION_EXPORT_WORD,
	ACTION_TEMPLATES_AND_IMPORT,
	ACTION_TEMPLATES,
	ACTION_IMPORT,
	ACTION_CONVERT_TO_BLOG,
	ACTION_CONVERT_TO_FOLDER,
	ACTION_CONVERT_TO_PAGE,
	ACTION_CONVERT_TO_MARKDOWN,
	ACTION_CONVERT_TO_RICH_TEXT,
	ACTION_WHITEBOARD_PREFERENCES,
	ACTION_WHITEBOARD_EXPORT,
	ACTION_WHITEBOARD_FIND_REPLACE,
	ACTION_WHITEBOARD_IMPORT,
	ACTION_PRESENT_CONTENT,
	SWITCH_TO_LIVE_EDIT_WEB_ITEM,
	ACTION_REVERT,
	ACTION_REVERT_TO_LEGACY_EDITOR,
	ACTION_WHITEBOARD_FULLSCREEN,
	ACTION_WHITEBOARD_DOCUMENTATION,
	ACTION_WHITEBOARD_TEMPLATES,
	ACTION_WHITEBOARD_TO_LIVE_DOC_WITH_ROVO,
	ACTION_FAVOURITE_CONTENT,
	ACTION_FAVOURITE_SPACE,
	ACTION_WATCH_CONTENT,
	ACTION_SMART_FOLDER_SYNC,
	ACTION_ASK_TO_EDIT_CONTENT,
	ACTION_PREVIEW_CONTENT,
	ACTION_MANAGE_CONTENT,
	ACTION_AUTOMATIONS,
	ACTION_ADVANCED,
	ACTION_DATABASE_COPY_STRUCTURE,
	ACTION_DATABASE_UNDO,
	ACTION_DATABASE_REDO,
	ACTION_DATABASE_TEMPLATES,
	ACTION_DATABASE_EXPORT,
	ACTION_DATABASE_LOCK_STRUCTURE,
	ACTION_DATABASE_GET_HELP,
	ACTION_DATABASE_DEVTOOLS,
	ACTION_EMBED_REPLACE_LINK,
	ACTION_PAGES_PERMISSIONS_LINK,
	ACTION_EXPORT_CONTENT,
	ACTION_SLIDES_EXPORT,
	ACTION_CONVERT_CONTENT,
	ACTION_COMPANY_HUB_FEEDBACK,
	ACTION_EDITOR_UNDO,
	ACTION_EDITOR_REDO,
	ACTION_EDITOR_FIND_REPLACE,
} from './moreActionsConstants.ts';

// --- Nested / Combined Actions ---
const COMBINED_ACTION_ARCHIVE = 'combined-action-archive';

const isWebItem = (item: FormattedWebItem) => {
	return typeof item === 'object' && 'id' in item;
};

const i18n = defineMessages({
	triggerLabel: {
		id: 'content-types-header.content-types.more-actions.label',
		defaultMessage: 'More actions',
		description: 'A label for the button that opens a menu providing additional actions for a page',
	},
	tooltip: {
		id: 'content-types-header.content-types.more-actions.disabled-tooltip',
		defaultMessage: 'Coming soon',
		description:
			'Tooltip text displayed when hovering over disabled actions in the more actions menu',
	},
	templatesImportLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.templates',
		description: 'The label of Templates & import button inside ellipsis dropdown',
		defaultMessage: 'Templates and import',
	},
	templatesLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.templates-only',
		description: 'The label of Templates button inside ellipsis dropdown',
		defaultMessage: 'Templates',
	},
	importLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.import-only',
		description: 'The label of Import button inside ellipsis dropdown',
		defaultMessage: 'Import',
	},
	presentLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.present',
		defaultMessage: 'Present',
		description: 'The label of the Present button inside ellipsis dropdown',
	},
	revertLabel: {
		id: 'content-types-header.revert.label',
		description: 'The label of revert to previous button inside ellipsis dropdown',
		defaultMessage: 'Revert to previous version',
	},
	revertEditor: {
		id: 'content-types-header.revert.editor.label',
		description: 'The label of revert to legacy editor button inside ellipsis dropdown',
		defaultMessage: 'Revert to legacy editor',
	},
	help: {
		id: 'content-types-header.ellipsis.open.ellipsis.help',
		description: 'The label of the Help button inside the ellipsis dropdown',
		defaultMessage: 'Help',
	},
	previewLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.preview',
		defaultMessage: 'Preview',
		description: 'The label of the Preview button inside the ellipsis dropdown',
	},
	manageLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.manage',
		defaultMessage: 'Manage',
		description: 'The label of the Manage button inside the ellipsis dropdown',
	},
	versionHistoryLabel: {
		id: 'content-types-header.version.history.label',
		description: 'The label of version history inside ellipsis dropdown',
		defaultMessage: 'Version history',
	},
	convertToPage: {
		id: 'content-types-header.ellipsis.open.ellipsis.convert-to-page',
		description:
			'The label for an item in the overflow menu to convert a live doc into a classic page',
		defaultMessage: 'Convert to page',
	},
	convertToMarkdown: {
		id: 'content-types-header.ellipsis.open.ellipsis.convert-to-markdown',
		description: 'The label for an item in the overflow menu to convert a page into markdown mode',
		defaultMessage: 'Convert to markdown',
	},
	convertToRichText: {
		id: 'content-types-header.ellipsis.open.ellipsis.convert-to-rich-text',
		description:
			'The label for an item in the overflow menu to convert a page from markdown mode back to the rich-text editor',
		defaultMessage: 'Convert to rich text',
	},
	companyHubFeedback: {
		id: 'content-types-header.ellipsis.open.ellipsis.hub.share-feedback',
		description: 'The label for an item in the overflow menu to provide feedback for company hub',
		defaultMessage: 'Give feedback on Company hub',
	},
	editorUndoLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.editor-undo',
		defaultMessage: 'Undo',
		description: 'The label of the Undo button inside the ellipsis dropdown',
	},
	editorRedoLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.editor-redo',
		defaultMessage: 'Redo',
		description: 'The label of the Redo button inside the ellipsis dropdown',
	},
	editorFindReplaceLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.editor-find-replace',
		defaultMessage: 'Find and replace',
		description: 'The label of the Find and replace button inside the ellipsis dropdown',
	},
});

const dropdownMenuContainerStyles = xcss({
	marginLeft: 'space.050',
	minWidth: '24px',
	display: 'flex',
	alignItems: 'center',
});

const dropdownMenuContentStyles = xcss({
	minWidth: '260px',
});

const dropdownTriggerBoxStyles = xcss({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const KeyboardShortcutIcon = styled.span({
	backgroundColor: `${token('color.background.neutral')}`,
	color: `${token('color.icon')}`,
	padding: `${token('space.025')} ${token('space.075')}`,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});

// NOTE: These styles are directly copied from ContentTools.tsx in @confluence/content-tools
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResolvedCommentsContainer = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResolvedCommentsLabelContainer = styled.div({
	flex: 2,
	marginRight: token('space.075'),
});

const RemovedSpotlightTarget = componentWithFG(
	'remove_unused_spotlight_targets',
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
	({ name, children }: { name: string } & PropsWithChildren<{}>) => (
		<SpotlightTarget name={name}>{children}</SpotlightTarget>
	),
);

export interface Item {
	id: string;
	text: string;
	shortcutText?: string;
	isDisabled?: boolean;
	showToggle?: {
		isDisabled: boolean;
		isChecked?: boolean;
	};
	onClick?: () => void;
	url?: string;
	target?: string;
}

export interface ItemGroup {
	id: string;
	items: Item[];
	text?: string;
}

const isItemGroup = (item: Item | ItemGroup): item is ItemGroup => {
	return !!(item as ItemGroup).items;
};

export interface MoreActions {
	items: (Item | ItemGroup)[][];
}

type MoreActionsComponentType = {
	moreActions: MoreActions | undefined | null;
	contentId: string;
	contentType: ShareContentType;
	contentMode?: string;
	spaceKey: string;
	allowedFeatures: HeaderFeaturesType;
	hasArchivePermission: boolean | undefined;
	// Use this for onboarding spotlights in the content types header.
	handleRegisterOnShowHeaderSpotlight?: (callback: () => void) => void;
	onClickOpenContentCreationPanel?: () => void;
	isContentCreationPanelOpen?: boolean;
	flags: FlagsStateContainer;
	showRevertDialog?: () => void;
	onPreviewClicked?: (...args: any[]) => void;
	hasLivePageEditPermission?: boolean;
	contentData: ContentTypesHeaderQueryNode | null | undefined;
	readOnly?: boolean;
	isConvertToFolderHighlighted?: boolean;
	setIsConvertToFolderHighlighted?: Dispatch<React.SetStateAction<boolean>>;
};

type ActionItem = {
	name: string;
	shouldRender: boolean | undefined;
	component: () => React.JSX.Element;
};

type ActionGroup = {
	key: string;
	items: ActionItem[];
};

const createActionsMap = (actions) => {
	const map = {};
	actions?.forEach((action) => {
		if (action?.id) {
			map[action.id] = action;
		}
	});
	return map;
};

const useOpenWatchMenuQueryParam = createSingleQueryParamHook('openWatchModal');

export const MoreActionsComponent = ({
	moreActions,
	contentId,
	contentType,
	contentMode,
	spaceKey,
	allowedFeatures,
	hasArchivePermission,
	flags,
	contentData,
	readOnly,
	isConvertToFolderHighlighted = false,
	setIsConvertToFolderHighlighted,
}: MoreActionsComponentType) => {
	const intl = useIntl();
	const history = useHistory();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const triggerAnalyticsEvent = useMemo(
		() => async (eventName) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: eventName,
					actionSubject: 'askToEditContent',
					source: 'moreActionsMenu',
					attributes: {
						contentId,
						contentType,
						isContentWrapper: true,
						spaceKey,
						contentMode: contentMode || 'view',
					},
				},
			}).fire();
		},
		[contentId, contentType, spaceKey, contentMode, createAnalyticsEvent],
	);

	const {
		saveDraft,
		onClickOpenContentCreationPanel,
		isContentCreationPanelOpen,
		contentCreationPanelMode,
		showRevertDialog,
		onPreviewClicked,
		hasEditPermission,
		validatePageAndOpenModal,
		canUndo: editorCanUndo,
		canRedo: editorCanRedo,
		undo: editorUndo,
		redo: editorRedo,
		activateFindReplace: editorActivateFindReplace,
		editorActions,
	} = useEditorObjectHeaderContext();

	const isPageOrBlogAutoConverted = useIsPageOrBlogAutoConverted({
		contentId,
		contentType,
	});

	const openCompanyHubFeedbackCollector = useFeedbackCollector({
		source: 'hubEditorEllipsisDropdown',
		type: FeedbackType.COMPANY_HUB,
	});

	const publishPage = validatePageAndOpenModal?.('OverflowMenuConvertToPage');

	const [
		preloadWebItemsQuery,
		{ data: webItemsData, called: isWebItemsQueryCalled, error, loading },
	] = useLazyQueryInterop<WebItemLocationQueryType, WebItemLocationQueryVariables>(
		WebItemLocationQuery,
		{
			variables: {
				contentId,
				location: TOOLS_MENU_CONTAINER,
				locations: [],
				spaceKey: null,
				version: null,
			},
		},
	);

	const {
		showMove: shouldShowMove,
		showArchive: shouldShowArchive,
		showCopy: shouldShowCopy,
		showClassificationMenuAction: shouldShowClassification,
	} = allowedFeatures;
	const [isCopyContentModalOpen, setIsCopyContentModalOpen] = useState(false);
	const [isMoveContentModalOpen, setIsMoveContentModalOpen] = useState(false);
	const [isShortcutTriggered, setIsShortcutTriggered] = useState(false);
	const { isMenuOpen, isCloseMenuDisabled } = useMoreActionsMenuState();
	const { setIsMenuOpen } = useMoreActionsMenuActions();
	const spaceId = useSpaceId();
	const handleSmartFolderStopSyncing = useCallback(() => {
		void setIsMenuOpen(false);
	}, [setIsMenuOpen]);

	const fireSmartFolderMenuAnalytics = useCallback(
		(actionSubjectId: SmartFolderQuickActionAnalyticsId) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'dropdownItem',
					actionSubjectId,
					source: 'moreActions',
					objectId: contentId,
					objectType: contentType,
					containerId: spaceId,
				},
			}).fire();
		},
		[contentId, contentType, createAnalyticsEvent, spaceId],
	);
	const { edition, isLoggedIn, isRovoEnabled } = useSessionData();
	const isFreeUser = edition === ConfluenceEdition.FREE;
	const isPremiumUser = edition === ConfluenceEdition.PREMIUM;
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { trackExperience } = useVersionHistoryExperienceTracking();
	const isCommentsEnabled = useIsCommentsDialogEnabled(contentType);
	const isVersionHistoryEnabled = useIsVersionHistoryEnabled(contentType);
	const { commentService } = useCommentService();
	const [nestedPopup, setNestedPopup] = useState<NestedMenuTypes | undefined>(undefined);
	const { showSidePanel } = useSidePanelStateActions();
	const isExternalCollaborator = useIsExternalCollaborator();
	const isSpaceOverviewPage = isSpaceOverview();
	const isPresentRendered = getIsPresentRendered(contentType, contentMode, isSpaceOverviewPage);
	const isLivePage = contentType === 'page' && contentMode === 'live';
	const {
		isMarkdownMode,
		isUpdating: isMarkdownModeUpdating,
		convertToMarkdown,
		convertToRichText,
		confirmConvertToMarkdown,
		confirmConvertToRichText,
	} = useMarkdownModeActions({ contentId, isLivePage, editorActions, flags });
	const [{ mode: livePageMode }] = useLivePageMode();
	const isLivePageViewOnlyPermissions = isLivePage && !hasEditPermission;
	const hasUpdateOperation = (type: string) =>
		contentData?.operations?.some(
			(operation) => operation?.targetType === type && operation?.operation === 'update',
		);
	const isAskToEditEnabled = (type: string) => !hasUpdateOperation(type);
	const isPageViewOnlyPermissions = contentType === 'page' && !hasUpdateOperation(contentType);
	const isCanvasViewOnlyPermissions =
		isCanvasContentType(contentType) && isAskToEditEnabled(contentType);
	const isDatabaseViewOnlyPermissions =
		contentType === 'database' && isAskToEditEnabled(contentType);
	const isBlogViewOnlyPermissions = contentType === 'blogpost' && isAskToEditEnabled(contentType);
	const isContentViewOnlyPermissions =
		isLivePageViewOnlyPermissions ||
		isPageViewOnlyPermissions ||
		isCanvasViewOnlyPermissions ||
		isDatabaseViewOnlyPermissions ||
		isBlogViewOnlyPermissions;
	const contentStatus = contentData?.status || undefined;
	const isArchived = contentData?.status === 'archived';
	const isStarringEnabledForContentType = useIsStarringEnabled(contentType, contentMode);
	const [{ theme }] = useThemingState();
	const openWatchModalQueryParam = Boolean(useOpenWatchMenuQueryParam());
	const { setQueryParams, push } = useRouteActions();
	const { activeSpotlight, resetTour } = useMoreActionsObjectSidebarTour({
		step: MoreActionsObjectSidebarTourSteps.FIRST,
	});
	const hasReadPermission = Boolean(
		contentData?.operations?.filter(
			(operation) => operation?.targetType === contentType && operation?.operation === 'read',
		).length,
	);

	// controls the rendering of the star menu item + the star shortcut listener. There is no starring for draft pages and blogs currently.
	const shouldRenderStarContentMenuItem =
		isStarringEnabledForContentType &&
		!!allowedFeatures.showFavourite &&
		!isSpaceOverviewPage &&
		hasReadPermission;

	const shouldRenderStarSpaceMenuItem = isSpaceOverviewPage && isLoggedIn;

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const isSmartFolderContent = isSmartFolder(contentData ?? {});
	const {
		isSmartFoldersEnabledForMilestone: isSmartFoldersM1Enabled,
		loading: isSmartFoldersLoading,
	} = useIsSmartFoldersEnabledForMilestone(spaceKey, 1, { skip: !isSmartFolderContent });

	const companyHubEllipsisButtons = [
		Buttons.DELETE_DRAFT,
		Buttons.MANAGE,
		Buttons.RESOLVED_COMMENTS,
		Buttons.VIEW_CHANGES,
	];

	const allowedEllipsisButtons = isCompanyHub
		? companyHubEllipsisButtons
		: [
				Buttons.PUBLISH_WITHOUT_NOTIFY,
				Buttons.DELETE_DRAFT,
				Buttons.EDIT_LABELS,
				Buttons.MOVE_PAGE,
				Buttons.PREVIEW,
				Buttons.RESOLVED_COMMENTS,
				Buttons.TEMPLATE_BROWSER,
				Buttons.VIEW_CHANGES,
				Buttons.SWITCH_TO_LIVE_EDIT,
				Buttons.CLASSIFY_CONTENT,
			];

	const isAllowed = (menuItem: Buttons) => allowedEllipsisButtons?.includes(menuItem) ?? true;
	const isModernEmbeddedConfluence = useEmbeddedModernHeader();

	const isConvertToMarkdownShortcutEligible =
		!isLivePageViewOnlyPermissions &&
		getIsConvertToMarkdownRendered(contentType, contentMode, isMarkdownMode);
	const isConvertToRichTextShortcutEligible =
		!isLivePageViewOnlyPermissions &&
		getIsConvertToRichTextRendered(contentType, contentMode, isMarkdownMode);
	const isMarkdownToggleShortcutEnabled =
		isConvertToMarkdownShortcutEligible || isConvertToRichTextShortcutEligible;

	// Single accelerator that toggles between convert-to-markdown and
	// convert-to-rich-text — driven directly from the shortcut so users
	// don't need to open the More Actions menu first. Reuses the same
	// handlers as the menu items so analytics, the lossy confirmation
	// dialog, and the BE flag flip behave identically.
	const handleToggleMarkdownModeShortcut = useCallback(() => {
		if (isMarkdownModeUpdating) {
			return;
		}
		if (isConvertToRichTextShortcutEligible) {
			void convertToRichText();
		} else if (isConvertToMarkdownShortcutEligible) {
			void convertToMarkdown();
		}
	}, [
		isMarkdownModeUpdating,
		isConvertToRichTextShortcutEligible,
		isConvertToMarkdownShortcutEligible,
		convertToRichText,
		convertToMarkdown,
	]);

	useEffect(() => {
		if (isConvertToFolderHighlighted) {
			void setIsMenuOpen(isConvertToFolderHighlighted);
			setNestedPopup(NestedMenuTypes.Convert);
		}
	}, [isConvertToFolderHighlighted, setIsMenuOpen]);

	useEffect(() => {
		if (openWatchModalQueryParam && contentType && contentMode && !loading) {
			if (getIsWatchRendered(contentType, contentMode) && !!allowedFeatures.showWatchDialog) {
				void setIsMenuOpen(true);
				setNestedPopup(NestedMenuTypes.Watch);
			}
			setQueryParams({ openWatchModal: null });
		}
	}, [
		openWatchModalQueryParam,
		setQueryParams,
		contentType,
		contentMode,
		loading,
		allowedFeatures,
		setIsMenuOpen,
	]);

	useEffect(() => {
		if (isShortcutTriggered && !loading) {
			if (
				webItemsData?.webItemSections?.some(
					(section) => section?.items?.length && section?.items[0]?.id === ACTION_PRESENT_CONTENT,
				)
			) {
				openMoreActionsMenuClick();
				simulateItemClick(ACTION_PRESENT_CONTENT);
			}
			setIsShortcutTriggered(false);
		}
	}, [isShortcutTriggered, webItemsData, loading]);

	const handleOpenChange = useCallback(
		({ isOpen }: OnOpenChangeArgs) => {
			// Prevent the menu from closing when a consumer has explicitly disabled close behaviour.
			// This is needed when a portal is active (e.g. a modal dialog or dropdown rendered outside
			// the menu's DOM tree), where outside-click events would otherwise cause the menu to close.
			if (!isOpen && isCloseMenuDisabled) return;

			// If a tour is currently in progress, prevent the menu from closing when the user clicks outside of it.
			if (activeSpotlight) return;

			void setIsMenuOpen(isOpen);
			if (!isOpen) {
				resetTour();
				setNestedPopup(undefined);
				// Reset the Convert to Folder highlight state when the menu is closed
				setIsConvertToFolderHighlighted?.(false);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'contentTypeHeaderMoreActions',
					containerType: 'space',
					containerId: spaceId,
					objectId: contentId,
					source: 'contentTypesHeader',
					attributes: {
						isContentWrapper: fg('confluence_frontend_content_wrapper'),
					},
				},
			}).fire();
		},
		[
			isCloseMenuDisabled,
			activeSpotlight,
			setIsMenuOpen,
			createAnalyticsEvent,
			spaceId,
			contentId,
			resetTour,
			setIsConvertToFolderHighlighted,
		],
	);

	const preloadWebItems = () => {
		void preloadWebItemsQuery();
	};

	const handleNestedMenuItemClick = (nestedMenuType: NestedMenuTypes) => {
		if (nestedPopup === nestedMenuType) {
			setNestedPopup(undefined);
		} else {
			setNestedPopup(nestedMenuType);
		}
	};

	const handleNestedMenuItemClose = () => {
		setNestedPopup(undefined);
	};

	const renderMoreActions = () => {
		if (!moreActions?.items?.length) {
			return;
		}

		return moreActions.items.map(
			(itemGroup, idx) =>
				itemGroup.length && (
					<DropdownItemGroup key={idx} hasSeparator>
						{itemGroup.map((item) => {
							if (isItemGroup(item)) {
								const { id: groupId, items: groupItems, text: groupText } = item;
								const handleOnOpenChange = ({ isOpen }: OnOpenChangeArgs) => {
									if (isOpen) {
										setNestedPopup(NestedMenuTypes.MoreSupport);
									} else {
										setNestedPopup(undefined);
									}
								};
								return (
									<DropdownMenu
										key={groupId}
										placement="left-start"
										trigger={({ triggerRef, ...triggerProps }) => (
											<ButtonItem
												{...triggerProps}
												ref={triggerRef}
												testId={`${groupId}-button`}
												iconAfter={
													<ChevronRightIcon
														color={token('color.icon.subtle')}
														label=""
														size="small"
													/>
												}
											>
												<span>{groupText}</span>
											</ButtonItem>
										)}
										isOpen={nestedPopup === NestedMenuTypes.MoreSupport}
										onOpenChange={handleOnOpenChange}
									>
										{groupItems.map((subItem) => {
											const {
												id: innerId,
												url: innerUrl,
												target: innerTarget,
												onClick: innerOnClick,
												shortcutText: innerShortcutText,
												text: innerText,
											} = subItem;
											return (
												<DropdownItem
													key={innerId}
													href={innerUrl}
													target={innerTarget}
													onClick={innerOnClick}
													elemAfter={
														innerShortcutText ? (
															<KeyboardShortcutIcon>{innerShortcutText}</KeyboardShortcutIcon>
														) : undefined
													}
												>
													{innerText}
												</DropdownItem>
											);
										})}
									</DropdownMenu>
								);
							}

							return <CustomAction item={item} key={item.id} />;
						})}
					</DropdownItemGroup>
				),
		);
	};

	const onDeleteClick = useCallback(
		(dialogs: any) => {
			experienceTracker.start({
				name: DELETE_PAGE_EXPERIENCE,
			});
			dialogs.showModal(DeleteNestedPagesActionLoader, {
				contentId,
				source: 'contentTypesHeader',
				shouldRedirectOnSuccess: true,
				contentType,
			});
			void setIsMenuOpen(false);
		},
		[contentId, experienceTracker, contentType, setIsMenuOpen],
	);

	const renderMoveContentModal = () => {
		return (
			<MovePageDialog
				contentId={contentId}
				spaceKey={spaceKey}
				onClose={() => setIsMoveContentModalOpen(false)}
				onCloseComplete={() => setIsMoveContentModalOpen(false)}
				referrer="content-types-header"
			/>
		);
	};

	const renderMovePageModal = (dialogs) => {
		experienceTracker.start({
			name: MOVE_PAGE_DIALOG_LOAD_EXPERIENCE,
		});
		dialogs.showDialog(MovePageDialog, {
			spaceKey,
			contentId,
			referrer: 'view-page-actions-menu',
			isMovingDraftVersion: contentMode === 'edit',
			isUnpublishedDraft: contentMode === 'draft',
		});
	};

	const renderMoveBlogModal = (dialogs) => {
		dialogs.showModal(MoveBlogModalLoader, {
			currentSpaceKey: spaceKey,
			blogPostId: contentId,
			isEditMode: contentMode === 'edit',
			isUnpublishedDraft: contentMode === 'draft',
		});
	};

	const renderCopyContentModal = () => {
		return expVal('confluence_modernized_copy_dialog', 'isEnabled', false) ? (
			<CopyPageDialog
				contentId={contentId}
				spaceKey={spaceKey}
				onClose={async () => setIsCopyContentModalOpen(false)}
			/>
		) : (
			<CopyPageTreeDialog
				contentId={contentId}
				spaceKey={spaceKey}
				onClose={async () => setIsCopyContentModalOpen(false)}
			/>
		);
	};

	const renderConvertMarkdownModeDialog = () => {
		return (
			<ConvertMarkdownModeDialogContainer
				onConfirmToMarkdown={confirmConvertToMarkdown}
				onConfirmToRichText={confirmConvertToRichText}
			/>
		);
	};

	const onArchiveClick = useCallback(
		(dialogs: DialogsStateContainer) => {
			if (isFreeUser) {
				dialogs.showDialog(ArchiveUpsellDialog);
			} else {
				experienceTracker.start({ name: ARCHIVE_PAGES_EXPERIENCE });
				dialogs.showDialog(ArchivePagesLoader, {
					contentId,
					source: 'contentTypesHeader',
				});
			}
			void setIsMenuOpen(false);
		},
		[isFreeUser, contentId, setIsMenuOpen, experienceTracker],
	);

	const handleViewVersionHistory = useCallback(() => {
		trackExperience({
			contentType,
			experience: 'sidePanel',
			task: 'start',
		});
		showSidePanel();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'versionHistory',
				source: 'moreActionsMenu',
				objectId: contentId,
				objectType: contentType,
				attributes: {
					isContentWrapper: fg('confluence_frontend_content_wrapper'),
				},
			},
		}).fire();
	}, [contentId, contentType, createAnalyticsEvent, showSidePanel, trackExperience]);

	const onRevertClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowRevert',
				source: 'overflowMenu',
			},
		}).fire();

		void saveDraft?.({
			isOnClose: false,
		});

		showRevertDialog?.();
	}, [createAnalyticsEvent, saveDraft, showRevertDialog]);

	const onRevertToLegacyEditorClick = useCallback(
		(dialogs) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'overflowRevertToLegacyEditor',
					source: 'overflowMenu',
				},
			}).fire();

			void saveDraft?.({
				isOnClose: false,
			});

			dialogs.showDialog(RevertEditorDialog, {
				contentId,
				onPageRevert: () =>
					onClickPageRevert(
						push,
						contentType as ContentTypeType,
						spaceKey,
						contentId,
						expValEquals('confluence_auto_convert_legacy_draft_fix_fe', 'isEnabled', true)
							? contentStatus
							: undefined,
					),
			});
		},
		[createAnalyticsEvent, saveDraft, contentId, push, contentStatus, contentType, spaceKey],
	);

	const onFeedbackClick = (dialogs: DialogsStateContainer, contentTypeForFeedback) => {
		dialogs.showModal(GlobalFeedbackCollector, {
			feedbackSource: CONTENT_TYPE_FEEDBACK_SOURCE[contentTypeForFeedback],
			showTypeField: true,
			contentType: contentTypeForFeedback,
		});
	};

	const makeInlineCommentsDialogOpener = (
		dialogs: DialogsStateContainer,
		context: 'whiteboard' | 'database' | 'slide',
	) => {
		dialogs.showDialog(ResolvedInlineCommentsDialogLoader, {
			contentId,
			context,
			onReopenResolvedCommentHandler: (commentIds) => {
				commentService.onReopenResolvedComment(commentIds);
			},
		});
	};

	const makePresenterModeOpener = (enterPresenterMode) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'present',
				source: 'moreActions',
				objectId: contentId,
				containerId: spaceId,
				attributes: {
					currentThemePreference: theme,
					OStheme: getSystemPreferredColorScheme(),
				},
			},
		}).fire();

		experienceTracker.start({
			name: PRESENTER_MODE_VIEW_EXPERIENCE,
		});

		enterPresenterMode();
	};

	const makePreviewContentHandler = (onPreviewClicked) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'previewPage',
				source: 'moreActions',
				objectId: contentId,
				objectType: contentType,
			},
		}).fire();

		onPreviewClicked(false);
	};

	const openLinkToThisPageDialog = (dialogs) => () => {
		dialogs.showModal(LinkToThisPageDialog, { contentId });
	};

	const primaryActionData = ({ webItemsMap, customActionsMap }): ActionGroup => {
		const primaryActionsItems = [
			{
				name: ACTION_FAVOURITE_SPACE,
				shouldRender: shouldRenderStarSpaceMenuItem,
				component: () => <SpaceStarMenuItemWithQuery spaceKey={spaceKey} />,
			},
			{
				name: ACTION_FAVOURITE_CONTENT,
				shouldRender: shouldRenderStarContentMenuItem && !isCompanyHub,
				component: () => (
					<FavouriteMenuItem
						contentId={contentId}
						contentTitle={contentData?.title || ''}
						contentMode={contentMode || ''}
					/>
				),
			},
			{
				name: ACTION_SMART_FOLDER_SYNC,
				shouldRender: isSmartFolderContent && !isSmartFoldersLoading && isSmartFoldersM1Enabled,
				// Wrap in `ErrorBoundary` so a chunk-load failure on the
				// dynamically-imported `SmartFolderQuickActionMenuItem` does
				// not crash the entire MoreActions menu. Mirrors the pattern
				// used elsewhere in this file (see `LazyPresenterModeContextConsumer`).
				component: () => (
					<ErrorBoundary attribution={Attribution.CONTENT_TYPES}>
						<LazySmartFolderQuickActionMenuItem
							/* TODO(CCINT-7600): wire appName from smart-folder content properties once exposed. */
							appName="Google Drive"
							folderId={contentId}
							fireAnalytics={fireSmartFolderMenuAnalytics}
							onConfirmStopSyncing={handleSmartFolderStopSyncing}
							compact={false}
						/>
					</ErrorBoundary>
				),
			},
			{
				name: ACTION_WATCH_CONTENT,
				shouldRender:
					getIsWatchRendered(contentType, contentMode) &&
					!!allowedFeatures.showWatchDialog &&
					!isCompanyHub,
				component: () => (
					<WatchSettingsItem
						isMenuOpen={nestedPopup === NestedMenuTypes.Watch}
						contentId={contentId}
						contentType={contentType}
						spaceId={spaceId}
						spaceKey={spaceKey}
						setIsMenuOpen={setIsMenuOpen}
						onMenuClose={handleNestedMenuItemClose}
						onClick={() => handleNestedMenuItemClick(NestedMenuTypes.Watch)}
					/>
				),
			},
			{
				name: ACTION_ASK_TO_EDIT_CONTENT,
				shouldRender: isLoggedIn && !isArchived && isContentViewOnlyPermissions,
				component: () => (
					<AskToEditMenuItem
						contentType={contentType}
						contentId={contentId}
						flags={flags}
						triggerAnalyticsEvent={triggerAnalyticsEvent}
						askToEditKey={ACTION_ASK_TO_EDIT_CONTENT}
					/>
				),
			},
			{
				name: ACTION_PREVIEW_CONTENT,
				shouldRender: getIsPreviewRendered(contentType, contentMode) && !isCompanyHub,
				component: () => (
					<DropdownItem
						key={ACTION_PREVIEW_CONTENT}
						onClick={makePreviewContentHandler(onPreviewClicked)}
						elemBefore={<ScreenIcon label="" />}
						elemAfter={
							<ShortcutKeysVisual
								id={ACTION_PREVIEW_CONTENT}
								shortcutKeys={[EDITOR2_SHORTCUTS.PREVIEW]}
							/>
						}
					>
						<FormattedMessage {...i18n.previewLabel} />
					</DropdownItem>
				),
			},
			{
				name: ACTION_MANAGE_CONTENT,
				shouldRender: isCompanyHub,
				component: () => (
					<DropdownItem
						key={ACTION_MANAGE_CONTENT}
						onClick={() =>
							history?.push(COMPANY_HUB_SETTINGS.toUrl(), { from: COMPANY_HUB_EDIT.name })
						}
						elemBefore={<SettingsIcon label="" />}
					>
						<FormattedMessage {...i18n.manageLabel} />
					</DropdownItem>
				),
			},
			{
				name: ACTION_WHITEBOARD_FULLSCREEN,
				shouldRender: !!customActionsMap[ACTION_WHITEBOARD_FULLSCREEN],
				component: () => {
					const item = customActionsMap[ACTION_WHITEBOARD_FULLSCREEN];
					return <CustomAction item={item} key={item.id} icon={<FullscreenEnterIcon label="" />} />;
				},
			},
			{
				name: ACTION_WHITEBOARD_PREFERENCES,
				shouldRender:
					getIsPreferencesRendered(contentType) &&
					!!customActionsMap[ACTION_WHITEBOARD_PREFERENCES],
				component: () => {
					const item = customActionsMap[ACTION_WHITEBOARD_PREFERENCES];
					return <CustomAction item={item} key={item.id} icon={<CustomizeIcon label="" />} />;
				},
			},
			{
				name: ACTION_PRESENT_CONTENT,
				shouldRender: isPresentRendered && !!webItemsMap[ACTION_PRESENT_CONTENT],
				component: () => (
					<ErrorBoundary
						attribution={Attribution.COMMENTS}
						key={webItemsMap[ACTION_PRESENT_CONTENT].completeKey}
					>
						<LazyPresenterModeContextConsumer
							key={`presenter-mode-menu-item-${webItemsMap[ACTION_PRESENT_CONTENT].completeKey}`}
						>
							{(enterPresenterMode) => (
								<DropdownItem
									// @ts-ignore id prop needed in order for shortcut to work properly
									id={ACTION_PRESENT_CONTENT}
									key={ACTION_PRESENT_CONTENT}
									onClick={makePresenterModeOpener(enterPresenterMode)}
									elemBefore={<PresenterModeIcon label="" />}
									elemAfter={
										<ShortcutVisualizer
											shortcut={
												isSafari()
													? UNIVERSAL_PRESENTATION_MODE_SHORTCUT_SAFARI
													: UNIVERSAL_PRESENTATION_MODE_SHORTCUT
											}
										/>
									}
								>
									<FormattedMessage {...i18n.presentLabel} />
								</DropdownItem>
							)}
						</LazyPresenterModeContextConsumer>
					</ErrorBoundary>
				),
			},
			{
				name: 'viewEditToggle',
				shouldShowSeparator: false,
				shouldRender: Boolean(contentMode === 'live' && hasEditPermission),
				component: () => (
					<LivePagesModeChanger
						contentId={contentId}
						flags={flags}
						analyticsSource="livePagesEllipsisDropdown"
						elemAfter={ShortcutKeysVisual}
					/>
				),
			},
		];

		const actionsToRender = primaryActionsItems.filter((item) => item.shouldRender);

		return {
			key: 'primary-actions',
			items: actionsToRender,
		};
	};

	const modifyActionData = ({ webItems, webItemsMap, customActionsMap }): ActionGroup => {
		const hasArchive =
			!isCompanyHub &&
			getIsArchiveRendered(contentType, contentMode) &&
			(hasArchivePermission || isFreeUser) &&
			allowedFeatures.showArchive &&
			!!webItemsMap[ACTION_ARCHIVE_CONTENT];
		const hasDelete = !!webItemsMap[ACTION_REMOVE_CONTENT];

		const modifyActionsToRender = [
			{
				name: ACTIONS_RESTORE_CONTENT,
				shouldRender: !!webItemsMap[ACTIONS_RESTORE_CONTENT],
				component: () => {
					return (
						<Subscribe to={[DialogsStateContainer]} key={`container-${ACTIONS_RESTORE_CONTENT}`}>
							{(dialogs: DialogsStateContainer) => (
								<DropdownItem
									key={ACTIONS_RESTORE_CONTENT}
									onClick={() => {
										createAnalyticsEvent({
											type: 'sendUIEvent',
											data: {
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'overflowRestore',
												source: 'overflowMenu',
												attributes: {
													isContentWrapper: fg('confluence_frontend_content_wrapper'),
												},
											},
										}).fire();
										dialogs.showModal(UnarchivePagesDialogLoader, {
											contentId,
											source: 'contentTypesHeader',
										});
									}}
									elemBefore={<WrenchIcon label="" />}
								>
									{webItemsMap[ACTIONS_RESTORE_CONTENT].label}
								</DropdownItem>
							)}
						</Subscribe>
					);
				},
			},
			{
				name: ACTION_WHITEBOARD_TO_LIVE_DOC_WITH_ROVO,
				// Render 'Create live doc with Rovo' action if:
				// - Content type is whiteboard
				// - Rovo is enabled
				// - fg for AIFC create (aifc_create_enabled) is enabled
				// - This action(whiteboard_to_live_doc_header_action_enabled) is enabled
				shouldRender: contentType === 'whiteboard' && isRovoEnabled && fg('aifc_create_enabled'),
				component: () => {
					return fg('whiteboard_to_live_doc_header_action_enabled') ? (
						<WhiteboardToLiveDocMenuItem spaceKey={spaceKey} contentId={contentId} />
					) : (
						<></>
					);
				},
			},
			{
				name: ACTION_COPY_CONTENT,
				shouldRender:
					allowedFeatures.showCopy &&
					getIsCopyRendered(contentType, contentMode) &&
					(!!webItemsMap[ACTION_COPY_CONTENT] ||
						!!customActionsMap[ACTION_DATABASE_COPY_STRUCTURE]),
				component: () => {
					// for exposure only, string value is determined by BE
					manuallyLogExpExposure('make_a_copy_cta');

					if (customActionsMap[ACTION_DATABASE_COPY_STRUCTURE]) {
						return (
							<NestedDatabaseCopyMenu
								isNestedMenuOpen={nestedPopup === NestedMenuTypes.CopyDatabase}
								handleClick={() => handleNestedMenuItemClick(NestedMenuTypes.CopyDatabase)}
								copyDatabaseItem={webItemsMap[ACTION_COPY_CONTENT]}
								copyStructureItem={customActionsMap[ACTION_DATABASE_COPY_STRUCTURE]}
								setIsCopyContentModalOpen={setIsCopyContentModalOpen}
								contentMode={contentMode}
								handleNestedMenuItemClose={handleNestedMenuItemClose}
								closeParentMenu={() => setIsMenuOpen(false)}
							/>
						);
					} else {
						return (
							<CopyMenuItem
								setIsCopyContentModalOpen={setIsCopyContentModalOpen}
								closeParentMenu={() => setIsMenuOpen(false)}
								item={webItemsMap[ACTION_COPY_CONTENT]}
							/>
						);
					}
				},
			},
			{
				name: ACTION_MOVE_CONTENT,
				shouldRender:
					!isLivePageViewOnlyPermissions &&
					allowedFeatures.showMove &&
					!isCompanyHub &&
					getIsMoveRendered(contentType, contentMode) &&
					!!webItemsMap[ACTION_MOVE_CONTENT],
				component: () => (
					<Subscribe to={[DialogsStateContainer]} key={`container-${ACTION_MOVE_CONTENT}`}>
						{(dialogs: DialogsStateContainer) => (
							<DropdownItem
								key={ACTION_MOVE_CONTENT}
								onClick={() => renderMovePageModal(dialogs)}
								elemBefore={<ArrowRightIcon label="" />}
							>
								{webItemsMap[ACTION_MOVE_CONTENT].label}
							</DropdownItem>
						)}
					</Subscribe>
				),
			},
			{
				name: ACTION_MOVE_BLOGPOST,
				shouldRender:
					allowedFeatures.showMove &&
					getIsMoveRendered(contentType, contentMode) &&
					!!webItemsMap[ACTION_MOVE_BLOGPOST],
				component: () => (
					<Subscribe to={[DialogsStateContainer]} key={`container-${ACTION_MOVE_BLOGPOST}`}>
						{(dialogs: DialogsStateContainer) => (
							<DropdownItem
								key={ACTION_MOVE_BLOGPOST}
								onClick={() => renderMoveBlogModal(dialogs)}
								elemBefore={<ArrowRightIcon label="" />}
							>
								{webItemsMap[ACTION_MOVE_BLOGPOST].label}
							</DropdownItem>
						)}
					</Subscribe>
				),
			},
			{
				// Legacy "Templates and import" when cc_snippets is OFF; when cc_snippets is ON, only for non-page (page gets separate items)
				name: ACTION_TEMPLATES_AND_IMPORT,
				shouldRender:
					// Legacy when cc_snippets off; when cc_snippets on, only show for non-page
					(!expVal('cc_snippets', 'isEnabled', false) || contentType !== 'page') &&
					getIsTemplatesRendered(contentType, contentMode, readOnly) &&
					!isContentCreationPanelOpen &&
					!isLivePageViewOnlyPermissions &&
					// if canvas content type (whiteboard/slide) we allow templates and imports if custom actions are available
					(!isCanvasContentType(contentType) ||
						!!customActionsMap[ACTION_WHITEBOARD_TEMPLATES] ||
						!!customActionsMap[ACTION_WHITEBOARD_IMPORT]),
				component: () => {
					if (customActionsMap[ACTION_DATABASE_TEMPLATES]) {
						customActionsMap[ACTION_DATABASE_TEMPLATES].text = intl.formatMessage(
							i18n.templatesImportLabel,
						);
						return (
							<CustomAction
								item={customActionsMap[ACTION_DATABASE_TEMPLATES]}
								key={ACTION_DATABASE_TEMPLATES}
								icon={<DownloadIcon label="" />}
							/>
						);
					} else if (
						customActionsMap[ACTION_WHITEBOARD_TEMPLATES] ||
						customActionsMap[ACTION_WHITEBOARD_IMPORT]
					) {
						const nestedItems: ExpandableNestedMenuItemItemType[] = [];
						const createNestedItem = (actionKey: string) => {
							const action = customActionsMap[actionKey];
							if (action) {
								nestedItems.push({
									...action,
									customComponent: () => <CustomAction item={action} key={action.id} />,
								});
							}
						};

						createNestedItem(ACTION_WHITEBOARD_TEMPLATES);
						createNestedItem(ACTION_WHITEBOARD_IMPORT);

						return (
							<ExpandableNestedMenuItem
								label={<FormattedMessage {...i18n.templatesImportLabel} />}
								isNestedMenuOpen={nestedPopup === NestedMenuTypes.TemplatesAndImport}
								onClick={() => handleNestedMenuItemClick(NestedMenuTypes.TemplatesAndImport)}
								iconBefore={<DownloadIcon label="" />}
								items={nestedItems}
								onMenuClose={handleNestedMenuItemClose}
							/>
						);
					} else {
						return (
							<DropdownItem
								key={ACTION_TEMPLATES_AND_IMPORT}
								onClick={() => onClickOpenContentCreationPanel?.()}
								elemBefore={<DownloadIcon label="" />}
							>
								<FormattedMessage {...i18n.templatesImportLabel} />
							</DropdownItem>
						);
					}
				},
			},
			{
				// "Templates" menu item - only shown when cc_snippets is ON and page (non-page uses legacy)
				// Shows when panel is closed OR panel is open in import mode
				name: ACTION_TEMPLATES,
				shouldRender:
					contentType === 'page' &&
					getIsTemplatesRendered(contentType, contentMode, readOnly) &&
					!isLivePageViewOnlyPermissions &&
					(!isContentCreationPanelOpen || contentCreationPanelMode === PANEL_MODE_IMPORT) &&
					expVal('cc_snippets', 'isEnabled', false),
				component: () => (
					<DropdownItem
						key={ACTION_TEMPLATES}
						onClick={() => onClickOpenContentCreationPanel?.(PANEL_MODE_TEMPLATES)}
						elemBefore={<TemplateIcon label="" />}
					>
						<FormattedMessage {...i18n.templatesLabel} />
					</DropdownItem>
				),
			},
			{
				// "Import" menu item - only shown when cc_snippets is ON and page (non-page uses legacy)
				// Shows when panel is closed OR panel is open in templates mode (and import is not disabled)
				name: ACTION_IMPORT,
				shouldRender:
					contentType === 'page' &&
					!isModernEmbeddedConfluence && // Import is disabled in embedded Confluence
					getIsTemplatesRendered(contentType, contentMode, readOnly) &&
					!isLivePageViewOnlyPermissions &&
					(!isContentCreationPanelOpen || contentCreationPanelMode === PANEL_MODE_TEMPLATES) &&
					expVal('cc_snippets', 'isEnabled', false),
				component: () => (
					<DropdownItem
						key={ACTION_IMPORT}
						onClick={() => onClickOpenContentCreationPanel?.(PANEL_MODE_IMPORT)}
						elemBefore={<DownloadIcon label="" />}
					>
						<FormattedMessage {...i18n.importLabel} />
					</DropdownItem>
				),
			},
			{
				name: SWITCH_TO_LIVE_EDIT_WEB_ITEM,
				shouldRender:
					getIsSwitchToLiveEditRendered(contentType, contentMode) &&
					!!webItemsMap[SWITCH_TO_LIVE_EDIT_WEB_ITEM],
				component: () => (
					<ConvertToLivePageItem
						item={webItemsMap[SWITCH_TO_LIVE_EDIT_WEB_ITEM]}
						contentMode={contentMode}
						flags={flags}
						spaceId={spaceId}
						spaceKey={spaceKey}
						contentId={contentId}
						closeParentMenu={() => setIsMenuOpen(false)}
					/>
				),
			},
			{
				name: ACTION_CONVERT_TO_PAGE,
				shouldRender:
					getIsConvertToPageRendered(contentType, contentMode) && !isLivePageViewOnlyPermissions,
				component: () => (
					<DropdownItem
						key={ACTION_CONVERT_TO_PAGE}
						onClick={publishPage}
						elemBefore={<ChangesIcon label="" />}
					>
						<FormattedMessage {...i18n.convertToPage} />
					</DropdownItem>
				),
			},
			{
				name: ACTION_CONVERT_TO_MARKDOWN,
				shouldRender: isConvertToMarkdownShortcutEligible,
				component: () => (
					<DropdownItem
						key={ACTION_CONVERT_TO_MARKDOWN}
						onClick={convertToMarkdown}
						isDisabled={isMarkdownModeUpdating}
						elemBefore={<MarkdownIcon label="" />}
						elemAfter={
							<ShortcutKeysVisual
								id={ACTION_CONVERT_TO_MARKDOWN}
								shortcutKeys={[UNIVERSAL_TOGGLE_MARKDOWN_MODE_SHORTCUT]}
							/>
						}
					>
						<FormattedMessage {...i18n.convertToMarkdown} />
					</DropdownItem>
				),
			},
			{
				name: ACTION_CONVERT_TO_RICH_TEXT,
				shouldRender: isConvertToRichTextShortcutEligible,
				component: () => (
					<DropdownItem
						key={ACTION_CONVERT_TO_RICH_TEXT}
						onClick={convertToRichText}
						isDisabled={isMarkdownModeUpdating}
						elemBefore={<PageLiveDocIcon label="" />}
						elemAfter={
							<ShortcutKeysVisual
								id={ACTION_CONVERT_TO_RICH_TEXT}
								shortcutKeys={[UNIVERSAL_TOGGLE_MARKDOWN_MODE_SHORTCUT]}
							/>
						}
					>
						<FormattedMessage {...i18n.convertToRichText} />
					</DropdownItem>
				),
			},
			{
				name: ACTION_CONVERT_CONTENT,
				shouldRender:
					getIsConvertRendered(contentType, contentMode, isSpaceOverviewPage) &&
					(!!webItemsMap[ACTION_CONVERT_TO_BLOG] ||
						!!webItemsMap[ACTION_CONVERT_TO_FOLDER] ||
						!!webItemsMap[SWITCH_TO_LIVE_EDIT_WEB_ITEM]),
				component: () => (
					<ConvertMenuItem
						webItemsMap={webItemsMap}
						nestedPopup={nestedPopup}
						handleNestedMenuItemClick={handleNestedMenuItemClick}
						spaceKey={spaceKey}
						contentId={contentId}
						handleNestedMenuItemClose={handleNestedMenuItemClose}
						flags={flags}
						spaceId={spaceId}
						contentMode={contentMode}
						closeParentMenu={() => setIsMenuOpen(false)}
						isConvertToFolderHighlighted={isConvertToFolderHighlighted}
					/>
				),
			},
			{
				name: ACTION_EXPORT_CONTENT,
				shouldRender:
					getIsExportRendered(contentType, contentMode) &&
					(!!webItemsMap[ACTION_EXPORT_WORD] ||
						!!webItemsMap[ACTION_EXPORT_PDF] ||
						(!!contentId && expValEquals('confluence_export_markdown', 'isEnabled', true))),
				component: () => (
					<ExportMenuItem
						webItemsMap={webItemsMap}
						handleNestedMenuItemClick={handleNestedMenuItemClick}
						nestedPopup={nestedPopup}
						handleNestedMenuItemClose={handleNestedMenuItemClose}
						closeParentMenu={() => setIsMenuOpen(false)}
						contentId={contentId}
						pageTitle={contentData?.title || ''}
						contentStatus={contentStatus}
					/>
				),
			},
			{
				name: ACTION_SLIDES_EXPORT,
				shouldRender: contentType === 'slide' && fg('cc-mui-slides-export'),
				component: () => (
					// Not using CustomAction component for now because we might want to support
					// nested menu in the future for different slide export types.
					// Ideally we want to extend CustomAction component to support nested menu (will revisit)
					<SlidesExportMenuItem closeParentMenu={() => setIsMenuOpen(false)} />
				),
			},
			{
				name: ACTION_WHITEBOARD_EXPORT,
				shouldRender: !!customActionsMap[ACTION_WHITEBOARD_EXPORT],
				component: () => {
					const item = customActionsMap[ACTION_WHITEBOARD_EXPORT];
					return (
						<CustomAction
							item={item}
							key={item.id}
							icon={<ChangesIcon label="" />}
							testId="whiteboard-export-button"
						/>
					);
				},
			},
			{
				name: ACTION_WHITEBOARD_FIND_REPLACE,
				shouldRender: !!customActionsMap[ACTION_WHITEBOARD_FIND_REPLACE],
				component: () => {
					const item = customActionsMap[ACTION_WHITEBOARD_FIND_REPLACE];
					return <CustomAction item={item} key={item.id} icon={<SearchIcon label="" />} />;
				},
			},
			{
				name: ACTION_DATABASE_EXPORT,
				shouldRender: !!customActionsMap[ACTION_DATABASE_EXPORT],
				component: () => {
					const item = customActionsMap[ACTION_DATABASE_EXPORT];
					return (
						<CustomAction
							item={item}
							key={item.id}
							icon={<ChangesIcon label="" />}
							testId="database-export-button"
						/>
					);
				},
			},
			{
				name: ACTION_ARCHIVE_CONTENT,
				shouldRender: hasArchive && !hasDelete,
				component: () => <ArchiveMenuItem onArchiveClick={onArchiveClick} />,
			},
			{
				name: ACTION_REMOVE_CONTENT,
				shouldRender: hasDelete && !hasArchive,
				component: () => (
					<DeleteMenuItem
						contentMode={contentMode}
						onDeleteClick={onDeleteClick}
						createAnalyticsEvent={createAnalyticsEvent}
					/>
				),
			},
			{
				name: COMBINED_ACTION_ARCHIVE,
				shouldRender: hasArchive && hasDelete,
				component: () => {
					return (
						<ArchiveDeleteMenuItem
							webItems={webItems}
							isNestedMenuOpen={nestedPopup === NestedMenuTypes.Archive}
							handleClick={() => handleNestedMenuItemClick(NestedMenuTypes.Archive)}
							onArchiveClick={onArchiveClick}
							onDeleteClick={onDeleteClick}
							contentMode={contentMode}
							createAnalyticsEvent={createAnalyticsEvent}
							handleNestedMenuItemClose={handleNestedMenuItemClose}
						/>
					);
				},
			},
		];

		const actionsToRender = modifyActionsToRender.filter((item) => item.shouldRender);

		return {
			key: 'modify-actions',
			items: actionsToRender,
		};
	};

	const getDatabaseActionItems = (customActionsMap: ActionGroup) => [
		{
			name: ACTION_DATABASE_UNDO,
			shouldRender: !!customActionsMap[ACTION_DATABASE_UNDO] && contentMode !== 'archived',
			component: () => {
				const item = customActionsMap[ACTION_DATABASE_UNDO];
				return <CustomAction item={item} key={item.id} icon={<UndoIcon label="" />} />;
			},
		},
		{
			name: ACTION_DATABASE_REDO,
			shouldRender: !!customActionsMap[ACTION_DATABASE_REDO] && contentMode !== 'archived',
			component: () => {
				const item = customActionsMap[ACTION_DATABASE_REDO];
				return <CustomAction item={item} key={item.id} icon={<RedoIcon label="" />} />;
			},
		},
		{
			name: ACTION_DATABASE_LOCK_STRUCTURE,
			shouldRender: !!customActionsMap[ACTION_DATABASE_LOCK_STRUCTURE],
			component: () => {
				const item = customActionsMap[ACTION_DATABASE_LOCK_STRUCTURE];
				return <CustomAction item={item} key={item.id} icon={<LockLockedIcon label="" />} />;
			},
		},
	];

	const secondaryActionData = ({ webItemsMap, customActionsMap, webItems }): ActionGroup => {
		// Reads edit permission from ContentTypesHeaderQuery instead of hasEditPermission, which may be undefined when viewing content despite user being an editor if content is not a live doc.
		const hasEditPermissionRevertToLegacyEditor = contentData?.operations?.some((operation) => {
			return operation?.targetType === contentType && operation?.operation === 'update';
		});
		const secondaryActionItems = [
			{
				name: Buttons.VIEW_CHANGES,
				shouldRender:
					!isLivePageViewOnlyPermissions &&
					getIsViewChangesRendered(contentType, contentMode) &&
					isAllowed(Buttons.VIEW_CHANGES),
				component: () => (
					<Subscribe to={[DialogsStateContainer]} key={Buttons.VIEW_CHANGES}>
						{(dialogs: DialogsStateContainer) => (
							<ViewChangesItem
								contentId={contentId}
								dialogs={dialogs}
								saveDraft={saveDraft}
								createAnalyticsEvent={createAnalyticsEvent}
							/>
						)}
					</Subscribe>
				),
			},
			{
				name: ACTION_REVERT,
				shouldRender: getIsRevertRendered(contentType, contentMode),
				component: () => (
					<DropdownItem
						key={ACTION_REVERT}
						onClick={onRevertClick}
						elemBefore={<UndoIcon label="" />}
					>
						<FormattedMessage {...i18n.revertLabel} />
					</DropdownItem>
				),
			},
			{
				name: ACTION_VIEW_RESOLVED_COMMENTS,
				shouldRender: isCommentsEnabled && !!webItemsMap[ACTION_VIEW_RESOLVED_COMMENTS],
				component: () => {
					const item = webItemsMap[ACTION_VIEW_RESOLVED_COMMENTS];
					return (
						<Subscribe to={[DialogsStateContainer]} key={`container-${item.id}`}>
							{(dialogs: DialogsStateContainer) => (
								<DropdownItem
									key={item.id}
									onClick={() => {
										if (isCanvasContentType(contentType) || contentType === 'database') {
											makeInlineCommentsDialogOpener(dialogs, contentType);
										}
									}}
									elemBefore={<CommentIcon label="" />}
								>
									<ResolvedCommentsContainer>
										<ResolvedCommentsLabelContainer>{item.label}</ResolvedCommentsLabelContainer>
										<ResolvedInlineCommentsCounterLoader contentId={contentId} />
									</ResolvedCommentsContainer>
								</DropdownItem>
							)}
						</Subscribe>
					);
				},
			},
			{
				name: ACTION_PAGES_PERMISSIONS_LINK,
				shouldRender: !isExternalCollaborator && !!webItemsMap[ACTION_PAGES_PERMISSIONS_LINK],
				component: () => (
					<RestrictionsDialogMenuPropsProvider contentId={contentId}>
						{(itemProps) => (
							<LazyContentToolsItem {...itemProps} elemBefore={<LockLockedIcon label="" />} />
						)}
					</RestrictionsDialogMenuPropsProvider>
				),
			},
			{
				name: ACTION_VIEW_VERSION_HISTORY,
				shouldRender: isVersionHistoryEnabled && !!webItemsMap[ACTION_VIEW_VERSION_HISTORY],
				component: () => {
					const item = webItemsMap[ACTION_VIEW_VERSION_HISTORY];
					return (
						<DropdownItem
							key={item.id}
							onClick={handleViewVersionHistory}
							elemBefore={<ClockIcon label="" />}
						>
							<FormattedMessage {...i18n.versionHistoryLabel} />
						</DropdownItem>
					);
				},
			},
			{
				name: ACTION_VIEW_PAGE_HISTORY,
				shouldRender:
					getIsPageHistoryRendered(contentType, contentMode) &&
					!!webItemsMap[ACTION_VIEW_PAGE_HISTORY],
				component: () => {
					const item = webItemsMap[ACTION_VIEW_PAGE_HISTORY];

					return (
						<ViewPageHistoryMenuItem
							itemId={item?.id}
							itemUrl={item?.url}
							label={intl.formatMessage(i18n.versionHistoryLabel)}
							isLivePage={isLivePage}
							contentType={contentType}
						/>
					);
				},
			},
			{
				name: CONTENT_OWNERSHIP_WEB_ITEM_ID,
				shouldRender:
					!isLivePageViewOnlyPermissions &&
					getIsContentOwnershipRendered(contentType) &&
					!!webItemsMap[CONTENT_OWNERSHIP_WEB_ITEM_ID],
				component: () => (
					<ContentOwnershipActionItem
						key={CONTENT_OWNERSHIP_WEB_ITEM_ID}
						item={webItemsMap[CONTENT_OWNERSHIP_WEB_ITEM_ID]}
						nestedPopup={nestedPopup}
						setNestedPopup={setNestedPopup}
						contentId={contentId}
						setIsMenuOpen={setIsMenuOpen}
					/>
				),
			},
			{
				name: ACTION_AUTOMATIONS,
				shouldRender:
					isPremiumUser &&
					isLoggedIn &&
					getIsAutomationsRendered(contentType, contentMode, isSpaceOverviewPage),
				component: () => (
					<AutomationsMenuItem
						experienceTracker={experienceTracker}
						contentId={contentId}
						spaceKey={spaceKey}
						pageTitle={contentData?.title || ''}
					/>
				),
			},
			{
				// revert to legacy editor item should show for view/edit page and blogpost dropdown if the content was autoconverted to fabric
				name: ACTION_REVERT_TO_LEGACY_EDITOR,
				shouldRender:
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					expVal('confluence_tinymce_deprecation_phase', 'value', 0) < 3 &&
					(hasEditPermissionRevertToLegacyEditor ||
						!expValEquals('confluence_edit_only_revert_to_legacy', 'isEnabled', true)) &&
					getIsRevertToLegacyEditorRendered(
						contentType,
						contentMode,
						isAllowed(Buttons.DELETE_DRAFT),
						expValEquals('confluence_auto_convert_legacy_draft_fix_fe', 'isEnabled', true),
					) &&
					isPageOrBlogAutoConverted,
				component: () => (
					<ErrorBoundary
						attribution={Attribution.CC_EDITOR_AI}
						key="revert-to-legacy-editor-menu-item"
					>
						<Subscribe to={[DialogsStateContainer]}>
							{(dialogs: DialogsStateContainer) => (
								<DropdownItem
									key={ACTION_REVERT}
									onClick={() => onRevertToLegacyEditorClick(dialogs)}
									elemBefore={<ArrowLeftIcon label="" />}
								>
									<FormattedMessage {...i18n.revertEditor} />
								</DropdownItem>
							)}
						</Subscribe>
					</ErrorBoundary>
				),
			},
			{
				name: ACTION_ADVANCED,
				shouldRender: getIsAdvancedRendered(contentType, contentMode),
				component: () => (
					<AdvancedMenuItem
						webItemsMap={webItemsMap}
						handleNestedMenuItemClick={handleNestedMenuItemClick}
						nestedPopup={nestedPopup}
						handleNestedMenuItemClose={handleNestedMenuItemClose}
						closeParentMenu={() => setIsMenuOpen(false)}
					/>
				),
			},
			getAppsMenuItem({
				isAppsRendered: getIsAppsRendered(contentType, contentMode),
				spaceKey,
				contentId,
				isMenuOpen,
				handleNestedMenuItemClick,
				nestedPopup,
				items: webItems,
				createAnalyticsEvent,
				handleNestedMenuItemClose,
				closeParentMenu: () => setIsMenuOpen(false),
			}),
			{
				name: ACTION_EMBED_REPLACE_LINK,
				shouldRender: !!customActionsMap[ACTION_EMBED_REPLACE_LINK],
				component: () => {
					const item = customActionsMap[ACTION_EMBED_REPLACE_LINK];
					return <CustomAction item={item} key={item.id} icon={<EditIcon label="" />} />;
				},
			},
		];

		const actionsToRender = secondaryActionItems.filter((item) => item.shouldRender);

		return {
			key: 'secondary-actions',
			items: actionsToRender,
		};
	};

	const databaseActionData = ({ customActionsMap }): ActionGroup => {
		const databaseActionItems = getDatabaseActionItems(customActionsMap);
		const actionsToRender = databaseActionItems.filter((item) => item.shouldRender);

		return {
			key: 'database-actions',
			items: actionsToRender,
		};
	};

	const editorActionData = (): ActionGroup => {
		if (!getIsUndoRedoFindRendered(contentType, contentMode, hasEditPermission, livePageMode)) {
			return {
				key: 'editor-actions',
				items: [],
			};
		}

		const editorActionItems = [
			{
				name: ACTION_EDITOR_UNDO,
				shouldRender: !!editorUndo && !fg('aifc_create_enabled'),
				component: () => {
					return (
						<DropdownItem
							key={ACTION_EDITOR_UNDO}
							isDisabled={!editorCanUndo}
							elemBefore={<UndoIcon label="" />}
							elemAfter={
								<ShortcutKeysVisual
									id={ACTION_EDITOR_UNDO}
									shortcutKeys={[EDITOR2_SHORTCUTS.UNDO]}
								/>
							}
							onClick={async () => {
								editorUndo?.();
							}}
						>
							<FormattedMessage {...i18n.editorUndoLabel} />
						</DropdownItem>
					);
				},
			},
			{
				name: ACTION_EDITOR_REDO,
				shouldRender: !!editorRedo && !fg('aifc_create_enabled'),
				component: () => {
					return (
						<DropdownItem
							key={ACTION_EDITOR_REDO}
							isDisabled={!editorCanRedo}
							elemBefore={<RedoIcon label="" />}
							elemAfter={
								<ShortcutKeysVisual
									id={ACTION_EDITOR_REDO}
									shortcutKeys={[EDITOR2_SHORTCUTS.REDO]}
								/>
							}
							onClick={async () => {
								editorRedo?.();
							}}
						>
							<FormattedMessage {...i18n.editorRedoLabel} />
						</DropdownItem>
					);
				},
			},
			{
				name: ACTION_EDITOR_FIND_REPLACE,
				shouldRender: !!editorActivateFindReplace,
				component: () => {
					return (
						<DropdownItem
							key={ACTION_EDITOR_FIND_REPLACE}
							elemBefore={<SearchIcon label="" />}
							elemAfter={
								<ShortcutKeysVisual
									id={ACTION_EDITOR_FIND_REPLACE}
									shortcutKeys={[EDITOR2_SHORTCUTS.FIND_REPLACE]}
								/>
							}
							onClick={async () => {
								editorActivateFindReplace?.();
							}}
						>
							<FormattedMessage {...i18n.editorFindReplaceLabel} />
						</DropdownItem>
					);
				},
			},
		];

		const actionsToRender = editorActionItems.filter((item) => item.shouldRender);
		return {
			key: 'editor-actions',
			items: actionsToRender,
		};
	};

	const feedbackActionData = ({ webItemsMap, customActionsMap, actions }): ActionGroup => {
		const additionalActionItems = [
			{
				name: ACTION_GIVE_FEEDBACK,
				shouldRender: getIsFeedbackRendered(contentType) && !!webItemsMap[ACTION_GIVE_FEEDBACK],
				component: () => {
					return (
						<Subscribe to={[DialogsStateContainer]}>
							{(dialogs: DialogsStateContainer) => (
								<DropdownItem
									key={ACTION_GIVE_FEEDBACK}
									onClick={() => {
										onFeedbackClick(dialogs, contentType);
									}}
									elemBefore={<MegaphoneIcon label="" />}
								>
									{webItemsMap[ACTION_GIVE_FEEDBACK].label}
								</DropdownItem>
							)}
						</Subscribe>
					);
				},
			},
			{
				name: ACTION_WHITEBOARD_DOCUMENTATION,
				shouldRender:
					getIsHelpRendered(contentType) && !!customActionsMap[ACTION_WHITEBOARD_DOCUMENTATION],
				component: () => {
					const items = actions.flat().map((action) => ({
						...action,
						customComponent: () => <CustomAction item={action} key={action.id} />,
					}));

					return (
						<ExpandableNestedMenuItem
							label={<FormattedMessage {...i18n.help} />}
							isNestedMenuOpen={nestedPopup === NestedMenuTypes.Help}
							onClick={() => handleNestedMenuItemClick(NestedMenuTypes.Help)}
							iconBefore={<QuestionCircleIcon label="" />}
							items={items}
							onMenuClose={handleNestedMenuItemClose}
						/>
					);
				},
			},
			{
				name: ACTION_DATABASE_GET_HELP,
				shouldRender:
					getIsHelpRendered(contentType) && !!customActionsMap[ACTION_DATABASE_GET_HELP],
				component: () => {
					customActionsMap[ACTION_DATABASE_GET_HELP].text = intl.formatMessage(i18n.help);
					const item = customActionsMap[ACTION_DATABASE_GET_HELP];
					return <CustomAction item={item} key={item.id} icon={<QuestionCircleIcon label="" />} />;
				},
			},
			{
				name: ACTION_DATABASE_DEVTOOLS,
				shouldRender: !!customActionsMap[ACTION_DATABASE_DEVTOOLS],
				component: () => {
					const item = customActionsMap[ACTION_DATABASE_DEVTOOLS];
					return <CustomAction item={item} key={item.id} />;
				},
			},
			{
				name: ACTION_COMPANY_HUB_FEEDBACK,
				shouldRender: isCompanyHub,
				component: () => (
					<DropdownItem
						key={ACTION_COMPANY_HUB_FEEDBACK}
						onClick={openCompanyHubFeedbackCollector}
						elemBefore={<MegaphoneIcon label="" />}
					>
						<FormattedMessage {...i18n.companyHubFeedback} />
					</DropdownItem>
				),
			},
		];

		const actionsToRender = additionalActionItems.filter((item) => item.shouldRender);

		return {
			key: 'additional-items',
			items: actionsToRender,
		};
	};

	const renderModifyContentActionsWebItems = (webItems: FormattedWebItem[]) => {
		return (
			<DropdownItemGroup>
				{webItems.map((item) => {
					if (isWebItem(item)) {
						if (shouldShowCopy && item?.id === ACTION_COPY_CONTENT) {
							// for exposure only, string value is determined by BE
							manuallyLogExpExposure('make_a_copy_cta');

							return (
								<DropdownItem key={item.id} onClick={() => setIsCopyContentModalOpen(true)}>
									{item.label}
								</DropdownItem>
							);
						}

						if (shouldShowMove && item?.id === ACTION_MOVE_CONTENT) {
							return (
								<DropdownItem key={item.id} onClick={() => setIsMoveContentModalOpen(true)}>
									{item.label}
								</DropdownItem>
							);
						}

						if (item?.id === ACTIONS_RESTORE_CONTENT) {
							return (
								<Subscribe to={[DialogsStateContainer]} key={`container-${item.id}`}>
									{(dialogs: DialogsStateContainer) => (
										<DropdownItem
											key={item.id}
											onClick={() => {
												dialogs.showDialog(UnarchivePagesDialogLoader, {
													contentId,
													source: 'contentTypesHeader',
												});
											}}
										>
											{item.label}
										</DropdownItem>
									)}
								</Subscribe>
							);
						}

						if (item?.id === ACTION_REMOVE_CONTENT) {
							return (
								<Subscribe to={[DialogsStateContainer]} key={`container-${item.id}`}>
									{(dialogs: DialogsStateContainer) => (
										<DropdownItem
											key={item.id}
											onClick={() => {
												onDeleteClick(dialogs);
											}}
										>
											{item.label}
										</DropdownItem>
									)}
								</Subscribe>
							);
						}

						if (item?.id === ACTION_ARCHIVE_CONTENT && hasArchivePermission && shouldShowArchive) {
							return (
								<Subscribe to={[DialogsStateContainer]} key={`container-${item.id}`}>
									{(dialogs: DialogsStateContainer) => (
										<DropdownItem
											key={item.id}
											onClick={() => {
												onArchiveClick(dialogs);
											}}
										>
											{item.label}
										</DropdownItem>
									)}
								</Subscribe>
							);
						}
					} else {
						return (
							<Tooltip
								content={<FormattedMessage {...i18n.tooltip} />}
								position="left"
								key={item.id}
							>
								<DropdownItem isDisabled>{item.label}</DropdownItem>
							</Tooltip>
						);
					}

					return null;
				})}
			</DropdownItemGroup>
		);
	};

	const renderPrimaryContentActionsWebItems = (webItems: FormattedWebItem[]) => {
		const webItemsToRender = webItems
			.map((item) => {
				if (isWebItem(item)) {
					if (
						item?.id === ACTION_VIEW_RESOLVED_COMMENTS &&
						isCommentsEnabled &&
						(isCanvasContentType(contentType) || contentType === 'database')
					) {
						return (
							<Subscribe to={[DialogsStateContainer]} key={`container-${item.id}`}>
								{(dialogs: DialogsStateContainer) => (
									<DropdownItem
										key={item.id}
										onClick={() => makeInlineCommentsDialogOpener(dialogs, contentType)}
									>
										<ResolvedCommentsContainer>
											<ResolvedCommentsLabelContainer>{item.label}</ResolvedCommentsLabelContainer>
											<ResolvedInlineCommentsCounterLoader contentId={contentId} />
										</ResolvedCommentsContainer>
									</DropdownItem>
								)}
							</Subscribe>
						);
					}

					if (item?.id === ACTION_VIEW_VERSION_HISTORY && isVersionHistoryEnabled) {
						return (
							<DropdownItem key={item.id} onClick={handleViewVersionHistory}>
								{item.label}
							</DropdownItem>
						);
					}

					if (item?.id === CONTENT_OWNERSHIP_WEB_ITEM_ID) {
						return (
							<ContentOwnershipActionItem
								key={`container-${item.id}`}
								item={item}
								nestedPopup={nestedPopup}
								setNestedPopup={setNestedPopup}
								contentId={contentId}
								setIsMenuOpen={setIsMenuOpen}
							/>
						);
					}
				}
				return null;
			})
			.filter(Boolean);

		const itemsToRender = shouldShowClassification
			? [<EllipsisClassificationMenuItem key="classify-content" />, ...webItemsToRender]
			: webItemsToRender;

		return itemsToRender.length > 0 ? (
			<DropdownItemGroup hasSeparator>{itemsToRender}</DropdownItemGroup>
		) : null;
	};

	const renderFeedbackWebItems = (webItems: FormattedWebItem[]) => {
		// If additional WebItems are added to the feedback WebSection in the monolith,
		// follow the structure of renderModifyContentActionsWebItems to map items into a collective
		// DropdownItemGroup rather than an individual group
		return webItems.map((item) => {
			if (isWebItem(item)) {
				if (
					item?.id === ACTION_GIVE_FEEDBACK &&
					contentType &&
					CONTENT_TYPE_FEEDBACK_SOURCE[contentType]
				) {
					return (
						<DropdownItemGroup hasSeparator key={`container-${item.id}`}>
							<Subscribe to={[DialogsStateContainer]}>
								{(dialogs: DialogsStateContainer) => (
									<DropdownItem
										key={item.id}
										onClick={() => {
											onFeedbackClick(dialogs, contentType);
										}}
									>
										{item.label}
									</DropdownItem>
								)}
							</Subscribe>
						</DropdownItemGroup>
					);
				}
			}

			return null;
		});
	};

	const renderActions = ({ webItems }: { webItems: FormattedWebItem[] }) => {
		const webItemsMap = createActionsMap(webItems);

		const customActionsToRender = moreActions?.items?.flat() ?? [];
		const customActionsMap = createActionsMap(customActionsToRender);
		const [, , , feedbackCustomActions] = moreActions?.items ?? [];

		const actionGroupsToRender: ActionGroup[] = [];
		const primaryActionGroup = primaryActionData({
			webItemsMap,
			customActionsMap,
		});
		if (primaryActionGroup.items.length > 0) {
			actionGroupsToRender.push(primaryActionGroup);
		}
		const editorActionGroup = editorActionData();
		if (editorActionGroup.items.length > 0) {
			actionGroupsToRender.push(editorActionGroup);
		}

		const databaseActionGroup = databaseActionData({
			customActionsMap,
		});

		if (databaseActionGroup.items.length > 0) {
			actionGroupsToRender.push(databaseActionGroup);
		}

		const secondaryActionGroup = secondaryActionData({
			webItemsMap,
			customActionsMap,
			webItems,
		});
		if (secondaryActionGroup.items.length > 0) {
			actionGroupsToRender.push(secondaryActionGroup);
		}
		const modifyActionGroup = modifyActionData({
			webItems,
			webItemsMap,
			customActionsMap,
		});
		if (modifyActionGroup.items.length > 0) {
			actionGroupsToRender.push(modifyActionGroup);
		}
		const feedbackActionGroup = feedbackActionData({
			webItemsMap,
			customActionsMap,
			actions: feedbackCustomActions ?? [],
		});
		if (feedbackActionGroup.items.length > 0) {
			actionGroupsToRender.push(feedbackActionGroup);
		}

		return (
			<>
				<Box xcss={dropdownMenuContentStyles}>
					{actionGroupsToRender.map((actionGroup, index) => (
						<DropdownItemGroup hasSeparator={index > 0} key={actionGroup.key}>
							{actionGroup.items.map((item) => item.component())}
						</DropdownItemGroup>
					))}
				</Box>
				<ExperienceSuccess name={CONTENT_OVERFLOW_MENU} />
			</>
		);
	};

	return (
		<>
			<ExperienceStart name={CONTENT_OVERFLOW_MENU} />
			{error && (
				<>
					<ExperienceStop name={CONTENT_OVERFLOW_MENU} error={error} />
					<ErrorDisplay error={error} />
				</>
			)}
			{isPresentRendered && (
				<GeneralShortcutListener
					deprecatedAccelerator={!isLivePage ? PRESENTATION_MODE_SHORTCUT : undefined}
					accelerator={
						isSafari()
							? UNIVERSAL_PRESENTATION_MODE_SHORTCUT_SAFARI
							: UNIVERSAL_PRESENTATION_MODE_SHORTCUT
					}
					listener={() => {
						if (!isWebItemsQueryCalled) {
							preloadWebItems();
						}
						setIsShortcutTriggered(true);
					}}
				/>
			)}
			{isMarkdownToggleShortcutEnabled && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_TOGGLE_MARKDOWN_MODE_SHORTCUT}
					listener={handleToggleMarkdownModeShortcut}
				/>
			)}
			{shouldRenderStarContentMenuItem && (
				<FavouriteContentShortcutListener
					contentId={contentId}
					contentTitle={contentData?.title || ''}
					contentMode={contentMode}
					contentType={contentType}
				/>
			)}
			{shouldRenderStarSpaceMenuItem && (
				<SpaceStarShortcutListener spaceKey={spaceKey} contentMode={contentMode} />
			)}
			{getIsWatchRendered(contentType, contentMode) &&
				fg('confluence_frontend_content_wrapper') && (
					<LazyWatchSettingsOverflowMenuShortcut
						contentId={contentId}
						contentType={contentType}
						openWatchSettingsMenu={() => {
							if (!isMenuOpen) {
								void setIsMenuOpen(true);
							}
							setNestedPopup(NestedMenuTypes.Watch);
						}}
					/>
				)}
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs: DialogsStateContainer) => (
					<>
						<GeneralShortcutListener
							deprecatedAccelerator={
								contentMode === 'view' || (contentMode === 'live' && livePageMode === 'view')
									? LINK_TO_THIS_PAGE_SHORTCUT
									: undefined
							}
							accelerator={UNIVERSAL_LINK_TO_THIS_PAGE_SHORTCUT}
							listener={openLinkToThisPageDialog(dialogs)}
						/>
						<LazyForgeContentTools
							spaceKey={spaceKey}
							contentId={contentId}
							onLoadComplete={() => {}}
							isDropdownMenuOpen={isMenuOpen}
							// This dummy placeholder makes sure that during loading, only one more actions button shows up
							lazyLoaderPlaceholderComponent=""
						>
							{(forgeMenuItems) =>
								forgeMenuItems.length > 0 &&
								forgeMenuItems.map((forgeMenuItem) => (
									<ForgeKeyboardShortcut
										key={forgeMenuItem?.props?.extension?.id}
										module={forgeMenuItem?.props?.extension}
										action={() => {
											void openForgeAppFromSubmenu({
												parentmenuId: '#more-actions-trigger',
												submenuId: "[data-testid='third-party-button']",
												forgeItemId: forgeMenuItem?.props?.extension?.id,
											});
										}}
									/>
								))
							}
						</LazyForgeContentTools>
					</>
				)}
			</Subscribe>
			{!isModernEmbeddedConfluence && (
				<Box
					xcss={[!fg('confluence_frontend_content_wrapper') && dropdownMenuContainerStyles]}
					onMouseEnter={preloadWebItems}
				>
					{/* TODO: Add `shouldRenderToParent` to resolve accessibility issues. See https://a11y.atlassian.net/browse/JCATS-788 */}
					<DropdownMenu
						isOpen={isMenuOpen}
						onOpenChange={handleOpenChange}
						trigger={(triggerProps) => <DropdownMenuTrigger triggerProps={triggerProps} />}
						placement="bottom-end"
					>
						<WebItemLocation
							data-testid="web-actions-test"
							contentId={contentId}
							location={TOOLS_MENU_CONTAINER}
							fetchPolicy="cache-and-network"
							renderWhenLoading={fg('confluence_frontend_content_wrapper')}
						>
							{({ webItems }) => {
								if (fg('confluence_frontend_content_wrapper')) {
									if (loading || !webItems || webItems.length === 0) {
										return <DropdownMenuContentSkeleton />;
									}
									return (
										<>
											<RemovedSpotlightTarget name={OBJECT_SIDEBAR_TOUR_TARGETS.MORE_ACTIONS_ITEMS}>
												{renderActions({ webItems })}
											</RemovedSpotlightTarget>
											<SpotlightTransition>{activeSpotlight}</SpotlightTransition>
										</>
									);
								} else {
									return (
										<Box padding="space.025">
											{renderModifyContentActionsWebItems(webItems)}
											{renderPrimaryContentActionsWebItems(webItems)}
											{renderFeedbackWebItems(webItems)}
											{renderMoreActions()}
										</Box>
									);
								}
							}}
						</WebItemLocation>
					</DropdownMenu>
				</Box>
			)}
			{isCopyContentModalOpen && renderCopyContentModal()}
			{isMoveContentModalOpen && renderMoveContentModal()}
			{expValEquals('cc-markdown-mode', 'isEnabled', true) && renderConvertMarkdownModeDialog()}
		</>
	);
};

interface DropdownMenuTriggerProps {
	triggerProps: CustomTriggerProps<HTMLButtonElement>;
}

const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({ triggerProps }) => {
	const { triggerRef, ...props } = triggerProps;
	const buttonRef = useRef<HTMLButtonElement>(null);
	const intl = useIntl();
	const { isPresenterModeActive, hasEnteredPresentMode } = usePresenterModeContext();
	const [{ isCollabOffline }] = useCollabNetworkStatus();

	// Network status is used when:
	// - isCollabOffline: The collab provider has detected a network disconnection
	// - platform_editor_offline_editing_web: Parent experiment for offline editing experience (must be enabled)
	// - platform_editor_offline_editing_wrapper_override: Kill switch to disable this feature (must be disabled)
	const isNetworkDisabled =
		isCollabOffline &&
		expVal('platform_editor_offline_editing_web', 'isEnabled', false) &&
		!fg('platform_editor_offline_editing_wrapper_override');

	useEffect(() => {
		// Only shift the focus back to "More actions" once the user has entered the present mode
		if (!isPresenterModeActive && hasEnteredPresentMode && buttonRef?.current) {
			buttonRef.current?.focus();
		}
	}, [isPresenterModeActive, hasEnteredPresentMode]);

	return (
		<Box xcss={dropdownTriggerBoxStyles}>
			<Tooltip position="bottom" content={<FormattedMessage {...i18n.triggerLabel} />}>
				<IconButton
					{...props}
					label={intl.formatMessage(i18n.triggerLabel)}
					icon={ShowMoreHorizontalIcon}
					ref={mergeRefs([triggerRef, buttonRef])}
					appearance="subtle"
					id="more-actions-trigger"
					interactionName="more-actions-overflow-menu"
					isDisabled={isNetworkDisabled}
				/>
			</Tooltip>
		</Box>
	);
};

const DropdownMenuContentSkeleton = () => (
	<UFOLoadHold name="more-actions-overflow-menu-hold">
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
		<DropdownItemSkeleton />
	</UFOLoadHold>
);

const DropdownItemSkeleton = () => (
	<DropdownItem>
		<Skeleton isShimmering borderRadius={0} width={200} height={20} />
	</DropdownItem>
);
